/* eslint you-dont-need-lodash-underscore/throttle: 0 */

// NOTE(omer.demirkan): this code is being duplicated in web/design/src/product/components/Ticker
// while we migrate the Ticker into bento. If you make any changes here
// please make the same change in the duplicate file and notify me.

// NOTE(omer.demirkan): This component is commonly used alongside
// charts. Hovering over them can mean rerendering hundreds of times
// a second. If you refactor this, please pay close attention to
// performance.

import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import Big from 'big.js'
import { Map as IMap } from 'immutable'
import throttle from 'lodash/throttle'
import React from 'react'
import Measure, { type ContentRect } from 'react-measure'
import { TransitionGroup } from 'react-transition-group'
import Transition, {
  type TransitionProps,
} from 'react-transition-group/Transition'

import { CssVars } from '../../themes'
import { Type } from '../Type'
import {
  type ActionTargets,
  type ColumnType,
  computeNextColumns,
} from './algo/computeNextColumns'
import { DELETE, INSERT, UPDATE } from './algo/constants'

const styles = {
  measureColumnsRoot: css({
    pointerEvents: 'none',
    position: 'absolute',
    visibility: 'hidden',
  }),
  tickerRoot: css({
    overflow: 'hidden',
    position: 'relative',
  }),
}

type Props = {
  /**
   * Applies aria-hidden to root element.
   */
  'aria-hidden'?: boolean
  /**
   * Determines the aria-label on the root element.
   */
  'aria-label'?: string
  /**
   * The list of characters that the value may contain.
   * Keep in mind that the order will change behavior,
   * earlier characters in the set will be higher in
   * the "wheel" of characters, and changing to it will
   * scroll the character wheel up.
   */
  characterSet?: string
  /**
   * The delay from changing value to start of animation.
   */
  spinDelay?: number | ((arg0: string) => number)
  /**
   * The string of the number to display. Number(value) must
   * not give NaN.
   */
  value: string
  /**
   * Determines if the ticker flashes when the value changes.
   */
  shouldFlashOnTransition?: boolean
  /**
   * Determines the character(s) that are prepended to the value.
   * By default, this is '$'. Note that for negative values, this
   * is after the '-'.
   */
  prepend?: string
}
type State = {
  columns: ColumnType[]
  transitionTime: number | null
}

// NOTE(omer.demirkan): Throttling the value instead of the
// update handler to avoid rerendering the entire component
// for every value change when hovering over a chart. This
// is to take full advantage of memoization.
export function ExperimentalTicker({
  characterSet = '9876543210-$,.',
  prepend = '$',
  shouldFlashOnTransition,
  spinDelay = 0,
  value,
  ...props
}: Props) {
  const throttledValue = useThrottleValue(value, 150)

  const prevValueRef = React.useRef(throttledValue)
  const currValueRef = React.useRef(throttledValue)
  if (currValueRef.current !== throttledValue) {
    prevValueRef.current = currValueRef.current
    currValueRef.current = throttledValue
  }

  const currNum = new Big(currValueRef.current)
  const prevNum = new Big(prevValueRef.current)
  const isLastChangeIncrease = currNum.cmp(prevNum) > 0

  let transitionColor: string | undefined
  if (shouldFlashOnTransition) {
    transitionColor = isLastChangeIncrease
      ? CssVars.semantic.positive.base
      : CssVars.semantic.negative.base
  }

  const displayValue = getDisplayValue(throttledValue, prepend)

  return (
    <Type.Display size="L">
      <TickerImpl
        {...props}
        characterSet={characterSet}
        spinDelay={spinDelay}
        transitionColor={transitionColor}
        value={displayValue}
      />
    </Type.Display>
  )
}

type UnthrottledTickerProps = {
  value: string
  characterSet: string
  spinDelay: number | ((arg0: string) => number)
  'aria-hidden'?: boolean
  'aria-label'?: string
  transitionColor?: string
}

const TickerImpl = React.memo(function _TickerImpl({
  'aria-hidden': ariaHidden,
  'aria-label': ariaLabel,
  characterSet,
  spinDelay,
  transitionColor,
  value,
}: UnthrottledTickerProps) {
  const columnCounterRef = React.useRef<number>(0)
  const generateId = React.useCallback(() => {
    const nextId = columnCounterRef.current
    columnCounterRef.current += 1
    return nextId
  }, [])

  // NOTE(omer.demirkan): columns and transitionTime haven't had their
  // state separated since migrating from a class component since they
  // are always updated together, to avoid needlessly causing two
  // renders when one is sufficient.
  const [{ columns, transitionTime }, setState] = React.useState<State>({
    columns: computeNextColumns([], value, generateId).columns.map(column => ({
      ...column,
      changed: false,
    })),
    transitionTime: 0,
  })

  React.useEffect(() => {
    if (typeof transitionTime !== 'number' || transitionTime <= 0) {
      return
    }

    function clearTransitioningState() {
      setState(prev => ({
        columns: prev.columns.map(column => ({ ...column, changed: false })),
        transitionTime: null,
      }))
    }
    const transitionTimeout = setTimeout(
      clearTransitioningState,
      transitionTime
    )

    return () => clearTimeout(transitionTimeout)
  }, [columns, transitionTime])

  const getSpinDelayForTarget = React.useCallback(
    (target: string) => {
      if (typeof spinDelay === 'function') {
        return spinDelay(target)
      }
      return spinDelay
    },
    [spinDelay]
  )

  const getTransitionDuration = React.useCallback(
    (actionTargets: ActionTargets) => {
      let duration = 0
      const updateActions = actionTargets[UPDATE]
      for (let i = 0; i < updateActions.length; i += 1) {
        const target = updateActions[i]
        const spinTime = getSpinDelayForTarget(target) + SPIN_DURATION
        duration = Math.max(duration, spinTime)
      }

      if (actionTargets[INSERT].length || actionTargets[DELETE].length) {
        duration = Math.max(duration, SLIDE_DURATION)
      }
      return duration
    },
    [getSpinDelayForTarget]
  )

  React.useEffect(() => {
    setState(prev => {
      const { actionTargets, columns: nextColumns } = computeNextColumns(
        prev.columns,
        value,
        generateId
      )
      const nextTransitionTime = document.hidden
        ? 0
        : getTransitionDuration(actionTargets)
      return {
        columns: nextColumns,
        transitionTime: nextTransitionTime,
      }
    })
  }, [value, generateId, getTransitionDuration])

  const isTransitioning =
    typeof transitionTime === 'number' && transitionTime > 0

  return (
    <div
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      css={styles.tickerRoot}
    >
      <StaticValue
        columns={columns}
        isTransitioning={isTransitioning}
        transitionColor={transitionColor}
      />
      <AnimatedColumns
        characterSet={characterSet}
        columns={columns}
        getSpinDelayForTarget={getSpinDelayForTarget}
        isTransitioning={isTransitioning}
        transitionColor={transitionColor}
      />
    </div>
  )
})

type StaticValueProps = {
  columns: ColumnType[]
  isTransitioning: boolean
  transitionColor?: string
}

const StaticValue = React.memo(function _StaticValue({
  columns,
  isTransitioning,
  transitionColor,
}: StaticValueProps) {
  return (
    <div css={{ opacity: isTransitioning ? 0 : 1 }}>
      {columns.map(column => (
        <Character
          key={column.id}
          isTransitioning={column.changed}
          transitionColor={transitionColor}
          value={column.target}
        />
      ))}
    </div>
  )
})

type AnimatedColumnsProps = {
  columns: ColumnType[]
  characterSet: string
  getSpinDelayForTarget: (target: string) => number
  isTransitioning: boolean
  transitionColor?: string
}

const AnimatedColumns = React.memo(function _AnimatedColumns({
  characterSet,
  columns,
  getSpinDelayForTarget,
  isTransitioning,
  transitionColor,
}: AnimatedColumnsProps) {
  const columnsRenderer = React.useCallback(
    (offsets: Offset[]) => {
      return (
        <TransitionGroup>
          {columns.map((column, index) => {
            const { x, y } = offsets[index]
            const spinDelay = getSpinDelayForTarget(column.target)
            return (
              <ColumnTransitionWrapper key={column.id} left={x}>
                <Column
                  characterSet={characterSet}
                  isTransitioning={column.changed}
                  offset={y}
                  spinDelay={spinDelay}
                  transitionColor={transitionColor}
                />
              </ColumnTransitionWrapper>
            )
          })}
        </TransitionGroup>
      )
    },
    [columns, characterSet, getSpinDelayForTarget, transitionColor]
  )

  return (
    <div
      aria-hidden
      css={{
        opacity: isTransitioning ? 1 : 0,
        pointerEvents: 'none',
      }}
    >
      <MeasureColumns characterSet={characterSet} columns={columns}>
        {columnsRenderer}
      </MeasureColumns>
    </div>
  )
})

type DimensionMap = IMap<string, number | undefined>

function getDomainOffsets(domain: string, heights: DimensionMap) {
  const domainOffsets = new Map<string, number>()
  if (domain.length) {
    const first = domain[0]
    domainOffsets.set(first, 0)
    for (let i = 1; i < domain.length; i += 1) {
      const current = domain[i]
      const prev = domain[i - 1]
      domainOffsets.set(
        current,
        Number(domainOffsets.get(prev)) + Number(heights.get(prev))
      )
    }
  }
  return domainOffsets
}

function getColumnOffsets(
  domain: string,
  columns: ColumnType[],
  widths: DimensionMap,
  heights: DimensionMap
) {
  const offsets: Offset[] = []
  if (columns.length) {
    const domainOffsets = getDomainOffsets(domain, heights)
    offsets.push({ x: 0, y: Number(domainOffsets.get(columns[0].target)) })
    for (let i = 1; i < columns.length; i += 1) {
      const col = columns[i]
      const prev = columns[i - 1]
      const prevX = offsets[i - 1].x
      offsets.push({
        x: prevX + Number(widths.get(prev.target)),
        y: Number(domainOffsets.get(col.target)),
      })
    }
  }
  return offsets
}

type MeasureColumnsProps = {
  children: (offsets: Offset[]) => React.ReactNode
  columns: ColumnType[]
  characterSet: string
}

type Offset = {
  x: number
  y: number
}

const MeasureColumns = React.memo(function _MeasureColumns({
  characterSet,
  children,
  columns,
}: MeasureColumnsProps) {
  // NOTE(omer.demirkan): Widths and heights have both states and refs.
  // - States to trigger a render after all characters in the domain
  //   are measured
  // - Refs are to collect the widths and heights while react-measure
  //   is calling onResize until all are measured.
  // This was a solution to React batching state updates so that only
  // the last onResize call would remain after; an issue I encountered
  // when migrating this to Bento. See the original here:
  // https://github.com/robinhoodmarkets/rh/blob/9f38317e47e9308ef0828fcd245d8212ba253348/web/web-app/src/silk/Ticker/AnimatedColumns/MeasureColumns.tsx#L81
  const widthsRef = React.useRef<{ [char: string]: number }>({})
  const [widths, setWidths] = React.useState<DimensionMap>(
    IMap<string, number | undefined>()
  )
  const heightsRef = React.useRef<{ [char: string]: number }>({})
  const [heights, setHeights] = React.useState<DimensionMap>(
    IMap<string, number | undefined>()
  )
  const handleMeasureCharacter = React.useCallback(
    (char: string) => {
      return (contentRect: ContentRect) => {
        widthsRef.current[char] = contentRect.bounds?.width as number
        heightsRef.current[char] = contentRect.bounds?.height as number

        if (Object.keys(widthsRef.current).length === characterSet.length) {
          // @ts-ignore
          setWidths(IMap(widthsRef.current))
          // @ts-ignore
          setHeights(IMap(heightsRef.current))
          widthsRef.current = {}
          heightsRef.current = {}
        }
      }
    },
    [characterSet]
  )

  const characterRenderer = React.useCallback(
    (char: string) => {
      return (
        <Measure bounds onResize={handleMeasureCharacter(char)}>
          {({ measureRef }: { measureRef: React.Ref<HTMLSpanElement>}) => <Character innerRef={measureRef} value={char} />}
        </Measure>
      )
    },
    [handleMeasureCharacter]
  )

  const offsets = getColumnOffsets(characterSet, columns, widths, heights)

  return (
    <div>
      <div css={styles.measureColumnsRoot}>
        <Column
          characterRenderer={characterRenderer}
          characterSet={characterSet}
        />
      </div>
      {children(offsets)}
    </div>
  )
})

type ColumnProps = {
  characterRenderer?: (
    char: string,
    overrideRootStyles?: CSSInterpolation
  ) => React.ReactNode
  characterSet: string
  offset?: number
  spinDelay?: number
  isTransitioning?: boolean
  transitionColor?: string
}

const Column = React.memo(function Column({
  characterRenderer,
  characterSet,
  isTransitioning,
  offset = 0,
  spinDelay = 0,
  transitionColor,
}: ColumnProps) {
  return (
    <div>
      <div
        css={{
          display: 'inline-block',
          transform: `translate(0, ${-offset}px)`,
          transition: isTransitioning
            ? `transform 300ms cubic-bezier(0.65, 0.05, 0.36, 1)`
            : undefined,
          transitionDelay: `${spinDelay}ms`,
        }}
      >
        {characterSet.split('').map(char => (
          <div key={char}>
            {characterRenderer ? (
              characterRenderer(char)
            ) : (
              <Character
                isTransitioning={isTransitioning}
                transitionColor={transitionColor}
                value={char}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
})

type ColumnTransitionWrapperProps = {
  children: React.ReactNode
  left: number
  isTransitioning?: boolean
} & Omit<TransitionProps, 'timeout'>

const ColumnTransitionWrapper = React.memo(function _ColumnTransitionWrapper({
  children,
  isTransitioning,
  left,
  ...props
}: ColumnTransitionWrapperProps) {
  return (
    <Transition timeout={SLIDE_DURATION} {...props}>
      {state => {
        return (
          <div
            css={{
              left: 0,
              opacity: state === 'entering' || state === 'entered' ? 1 : 0,
              position: 'absolute',
              top: 0,
              transform: `translate(${left}px, 0)`,
              transition: 'opacity 300ms ease, transform 300ms ease',
            }}
          >
            {children}
          </div>
        )
      }}
    </Transition>
  )
})

type CharacterProps = {
  value: string
  transitionColor?: string
  isTransitioning?: boolean
  innerRef?: React.Ref<HTMLSpanElement>
}

function Character({
  innerRef,
  isTransitioning,
  transitionColor,
  value,
}: CharacterProps) {
  return (
    <span
      ref={innerRef}
      css={{
        color: isTransitioning ? transitionColor : undefined,
        // NOTE(jare): The Ticker measures the size of each character by rendering it inside
        // of an inline-block level container and observing that container's size. We wrap
        // each character in an inline-block container here so that the space each character
        // takes up lines up with the measurements the Ticker's made. This also ensures that
        // the full string selectable by the browser as a single text block.
        display: 'inline-block',
        transition: 'color 300ms',
      }}
    >
      {value}
    </span>
  )
}

const SLIDE_DURATION = 300
const SPIN_DURATION = 300

function useThrottleValue<T>(value: T, intervalMs: number): T {
  const [throttledValue, setThrottledValue] = React.useState<T>(value)

  // NOTE(omer.demirkan): eslint is expecting an inline
  // function to understand the dependencies. Creating an
  // inline function () => throttle()() wouldn't allow for
  // throttling, so I've muted it.

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateThrottledValue = React.useCallback(
    throttle(setThrottledValue, intervalMs),
    [intervalMs]
  )
  const isMountedRef = React.useRef(false)
  React.useEffect(() => {
    // NOTE(omer.demirkan): To avoid an unnecessary call to
    // updateThrottledValue on mount. This is so updateThrottledValue
    // is interactive immediately for the first call instead of
    // being throttled with an unnecessary call on mount.
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return
    }
    updateThrottledValue(value)
  }, [value, updateThrottledValue])

  return throttledValue
}

function getDisplayValue(value: string, prepend: string) {
  const isNegative = value.startsWith('-')
  const absoluteValue = isNegative ? value.slice(1) : value
  const [flooredAbsoluteValue, decimalValue] = `${absoluteValue}.`.split('.')
  const flooredAbsoluteValueWithCommas = flooredAbsoluteValue.replace(
    // eslint-disable-next-line security/detect-unsafe-regex
    /\B(?=(\d{3})+(?!\d))/g,
    ','
  )
  return `${isNegative ? '-' : ''}${prepend}${flooredAbsoluteValueWithCommas}${
    decimalValue ? `.${decimalValue}` : ''
  }`
}
